import { useEffect } from "react";

/**
 * Scroll reveals, done once and cheaply.
 *
 * One observer for the whole page rather than one per element, and each element
 * is unobserved the moment it fires — a reveal that re-runs when you scroll back
 * up is a page that will not sit still, which reads as broken rather than alive.
 *
 * `prefers-reduced-motion` is not handled here. It is handled in CSS, where the
 * elements are simply visible from the start: motion preferences belong next to
 * the motion, and a JS check would leave anyone who changes the setting mid-visit
 * with content stuck at zero opacity.
 */
export function useReveal(): void {
  useEffect(() => {
    const targets = document.querySelectorAll("[data-reveal]");
    if (!targets.length) return;

    // No IntersectionObserver (or a very old browser): show everything. Content
    // hidden waiting for an API that never runs is the one unacceptable outcome.
    if (typeof IntersectionObserver === "undefined") {
      for (const el of targets) el.classList.add("shown");
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          e.target.classList.add("shown");
          io.unobserve(e.target);
        }
      },
      // Fire a little before the element reaches the fold, so it has finished
      // arriving by the time it is actually being read.
      { rootMargin: "0px 0px -12% 0px", threshold: 0.08 },
    );

    for (const el of targets) io.observe(el);
    return () => io.disconnect();
  }, []);
}
